/**
 * Tutors management page.
 * Tabbed list with {@link CollapseReplaceWrapper} for create/edit forms.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams, useNavigationType } from 'react-router-dom';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { TutorsDataTable } from '@/components/tutors/remote/TutorsDataTable';
import { CreateTutorView } from '@/components/tutors/remote/CreateTutorView';
import { EditTutorView } from '@/components/tutors/remote/EditTutorView';
import { ManagementPage } from '@/components/common/ManagementPage';
import { usePageActions } from '@/hooks/usePageActions';
import { usePermissions } from '@/hooks/usePermissions';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { useDetailsView } from '@/hooks/useDetailsView';
import { useViewMode } from '@/hooks/useViewMode';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/forms/Button';
import { onboardingKeys } from '@/hooks/useUsers';
import { exportExcel } from '@/services/remote/tutors.service';
import * as Icons from 'lucide-react';
import {
  type TutorRemoteTabView as TabView,
  TUTOR_REMOTE_TAB_LABELS as TAB_LABELS,
  TUTOR_REMOTE_TAB_COLORS as TAB_COLORS,
  TUTOR_REMOTE_TAB_ICONS as TAB_ICONS,
} from '@/components/tutors/tutor-configs';
import type { OnboardingFilters } from '@/types/remote/tutor.types';

/** Which form is mounted in the collapse wrapper. */
type ActiveForm = 'create' | 'edit' | null;

/** Remote tutors list with CollapseReplace create/edit. */
export function TutorsManagementPage() {
  const { canCreate, canEditRow, canExportExcel, canArchiveRow, canReactivateRow } =
    usePageActions('tutors');
  const { canViewInterface } = usePermissions();
  /** Subscribe so store updates re-render when session / report visibility changes. */
  useInterfaceElements('sessions');
  useInterfaceElements('reports');
  const { elements: sidebarElements } = useInterfaceElements('sidebar');
  const canViewSessions = canViewInterface('sessions');
  const canViewReports = canViewInterface('reports');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const companyId = user?.companyId ?? '';

  const navigationType = useNavigationType();
  /** Restore the last tab on browser back (`POP`); otherwise default to `tutors`. */
  const [activeTab, setActiveTab] = React.useState<TabView>(() => {
    if (navigationType === 'POP') {
      const saved = sessionStorage.getItem('dt-tab:tutors') as TabView | null;
      if (saved) return saved;
    }
    return 'tutors';
  });
  React.useEffect(() => {
    sessionStorage.setItem('dt-tab:tutors', activeTab);
  }, [activeTab]);
  const [activeForm, setActiveForm] = React.useState<ActiveForm>(null);
  const [editUserId, setEditUserId] = React.useState<string | null>(null);
  const [editStepKey, setEditStepKey] = React.useState<string | undefined>(undefined);

  const { viewMode, setViewMode } = useViewMode('tutors-management');
  const { openRowId, openRow, setOpenRow, closeRow } = useDetailsView();
  const [searchParams, setSearchParams] = useSearchParams();

  /** Deep-link: expand a tutor row via `?view=` (record id or user id), then strip the param. */
  React.useEffect(() => {
    const viewId = searchParams.get('view');
    if (viewId) {
      setOpenRow(viewId);
      searchParams.delete('view');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams]);

  /** Download the current filtered list as xlsx (blob URL is revoked after click). */
  const handleExportExcel = async (
    filters: Omit<OnboardingFilters, 'companyId' | 'page' | 'pageSize'>,
  ) => {
    try {
      const blob = await exportExcel(filters, companyId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tuteurs-remote-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  /** Close the form and drop the edit record / step so the next open starts clean. */
  const closeForm = React.useCallback(() => {
    setActiveForm(null);
    setEditUserId(null);
    setEditStepKey(undefined);
  }, []);

  /** Close the form and refresh onboarding lists. */
  const handleFormSuccess = React.useCallback(() => {
    setActiveForm(null);
    setEditUserId(null);
    setEditStepKey(undefined);
    void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
  }, [queryClient]);

  /** Stable JSX node for the form — avoids unmounting {@link EditTutorView} on re-renders. */
  const formContent = React.useMemo<React.ReactNode>(() => {
    if (activeForm === 'create') {
      return <CreateTutorView onSuccess={handleFormSuccess} onClose={closeForm} />;
    }
    if (activeForm === 'edit' && editUserId) {
      return (
        <EditTutorView
          key={editUserId}
          userId={editUserId}
          stepKey={editStepKey}
          onSuccess={handleFormSuccess}
          onClose={closeForm}
        />
      );
    }
    return null;
  }, [activeForm, editUserId, editStepKey, handleFormSuccess, closeForm]);

  const tabItems = React.useMemo(() => {
    const IconsMap = Icons as unknown as Record<string, Icons.LucideIcon>;
    const menuEl = sidebarElements.find((el) => el.elementId === 'menu-tutors');
    const MainIcon = IconsMap[menuEl?.config?.['iconName'] as string] ?? Icons.Circle;
    return Object.entries(TAB_LABELS).map(([id, label]) => {
      const tabId = id as TabView;
      const Icon = tabId === 'tutors' ? MainIcon : TAB_ICONS[tabId];
      return {
        id: tabId,
        label,
        icon: <Icon className="w-3 h-3" />,
        color: TAB_COLORS[tabId],
      };
    });
  }, [sidebarElements]);

  return (
    <ManagementPage
      title="Gestion des Tuteurs"
      subtitle="Créez, modifiez et gérez tous vos tuteurs"
      tabs={{
        items: tabItems,
        activeTab,
        onTabChange: (id) => {
          closeForm();
          closeRow();
          setActiveTab(id as TabView);
        },
      }}
      actions={
        canCreate ? (
          <Button
            preset="create-entity"
            onClick={() => {
              closeRow();
              setActiveForm('create');
            }}
          >
            Créer un tuteur
          </Button>
        ) : undefined
      }
    >
      <CollapseReplaceWrapper
        mode="replace"
        isOpen={activeForm !== null}
        collapsePosition="top"
        formContent={formContent}
      >
        <TutorsDataTable
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          expandedRowId={openRowId}
          onToggleRow={openRow}
          onCloseDetails={closeRow}
          onEditTutor={(id) => {
            setEditUserId(id);
            setEditStepKey(undefined);
            setActiveForm('edit');
            closeRow();
          }}
          onViewSessions={(record) => {
            void navigate('/sessions', {
              state: {
                tutorUserId: record.user?.id,
                personName: `${record.user?.firstName ?? ''} ${record.user?.lastName ?? ''}`.trim(),
              },
            });
          }}
          onViewReports={(record) => {
            void navigate('/reports', {
              state: {
                tutorUserId: record.user?.id,
                personName: `${record.user?.firstName ?? ''} ${record.user?.lastName ?? ''}`.trim(),
              },
            });
          }}
          enableFilters={true}
          activeTab={activeTab}
          pageActions={{
            canEditRow,
            canViewRow: true,
            canViewSessions,
            canViewReports,
            canArchiveRow,
            canReactivateRow,
          }}
          showExport={canExportExcel}
          onExportExcel={handleExportExcel}
          onEditSection={(stepKey, record) => {
            setEditUserId(record.userId);
            setEditStepKey(stepKey);
            setActiveForm('edit');
          }}
        />
      </CollapseReplaceWrapper>
    </ManagementPage>
  );
}
